import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, PageHeader, Select, StatusBadge } from '../components/ui';
import {
  issueReportService,
  type CreateIssueReportPayload,
  type IssueReport,
} from '../services/issue-report.service';
import {
  ISSUE_REPORT_CATEGORY_OPTIONS,
  getIssueReportCategoryLabel,
  getIssueReportPriorityMeta,
  getIssueReportReporterLabel,
  getIssueReportStatusMeta,
} from '../lib/issueReports';

const inputClassName =
  'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 shadow-sm transition-colors focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500';

const defaultFormState: CreateIssueReportPayload = {
  category: 'BUG',
  title: '',
  description: '',
  affectedArea: '',
  stepsToReproduce: '',
  expectedOutcome: '',
};

export default function ManufacturerIssueReportsPage() {
  const navigate = useNavigate();
  const [reports, setReports] = useState<IssueReport[]>([]);
  const [formState, setFormState] = useState<CreateIssueReportPayload>(defaultFormState);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    const loadReports = async () => {
      try {
        setLoading(true);
        setError(null);
        const nextReports = await issueReportService.getManufacturerIssueReports();
        setReports(nextReports);
      } catch (nextError: any) {
        setError(nextError.response?.data?.error || nextError.message || 'Failed to load issue reports');
      } finally {
        setLoading(false);
      }
    };

    void loadReports();
  }, []);

  const summary = useMemo(() => {
    return reports.reduce(
      (accumulator, report) => {
        accumulator.total += 1;
        if (report.status === 'SUBMITTED' || report.status === 'UNDER_REVIEW') {
          accumulator.reviewQueue += 1;
        }
        if (report.status === 'PLANNED' || report.status === 'IN_PROGRESS') {
          accumulator.inFlight += 1;
        }
        if (report.status === 'IMPLEMENTED') {
          accumulator.implemented += 1;
        }
        return accumulator;
      },
      { total: 0, reviewQueue: 0, inFlight: 0, implemented: 0 },
    );
  }, [reports]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    try {
      setSubmitting(true);
      setError(null);
      setSuccessMessage(null);

      const created = await issueReportService.submitIssueReport(formState);
      setReports((previous) => [created, ...previous]);
      setFormState(defaultFormState);
      setSuccessMessage('Issue report submitted. Your admin team can now review and prioritize it.');
    } catch (submitError: any) {
      setError(submitError.response?.data?.error || submitError.message || 'Failed to submit issue report');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <PageHeader
        title="Report Platform Issues"
        description="Capture bugs, workflow friction, and feature ideas for the Pureaum platform. Your admin team can review and prioritize them."
        actions={
          <Button variant="outline" onClick={() => navigate('/manufacturer')}>
            Back to Dashboard
          </Button>
        }
      />

      <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-4">
        <SummaryCard label="Total Reports" value={summary.total} toneClass="bg-slate-50 text-slate-700" />
        <SummaryCard label="Needs Review" value={summary.reviewQueue} toneClass="bg-amber-50 text-amber-700" />
        <SummaryCard label="Planned / In Progress" value={summary.inFlight} toneClass="bg-blue-50 text-blue-700" />
        <SummaryCard label="Implemented" value={summary.implemented} toneClass="bg-emerald-50 text-emerald-700" />
      </div>

      {(error || successMessage) && (
        <div className="mt-6 space-y-3">
          {error && <Alert variant="error">{error}</Alert>}
          {successMessage && <Alert variant="success">{successMessage}</Alert>}
        </div>
      )}

      <div className="mt-6 grid gap-6 xl:grid-cols-[minmax(0,1.05fr)_minmax(0,1.35fr)]">
        <section className="rounded-2xl bg-white p-6 shadow">
          <div className="mb-5">
            <h2 className="text-lg font-semibold text-gray-900">New Report</h2>
            <p className="mt-1 text-sm text-gray-600">
              Use this for bugs, confusing flows, improvement suggestions, or new feature requests.
            </p>
          </div>

          <form className="space-y-4" onSubmit={handleSubmit}>
            <Select
              label="Report Type"
              required
              value={formState.category}
              options={ISSUE_REPORT_CATEGORY_OPTIONS}
              onChange={(event) =>
                setFormState((previous) => ({ ...previous, category: event.target.value as CreateIssueReportPayload['category'] }))
              }
            />

            <FieldLabel text="Title" required />
            <input
              required
              minLength={5}
              maxLength={160}
              value={formState.title}
              onChange={(event) => setFormState((previous) => ({ ...previous, title: event.target.value }))}
              className={inputClassName}
              placeholder="Briefly summarize the issue or request"
            />

            <FieldLabel text="Affected Area" />
            <input
              value={formState.affectedArea}
              onChange={(event) => setFormState((previous) => ({ ...previous, affectedArea: event.target.value }))}
              className={inputClassName}
              placeholder="Example: Login, batch creation, analytics dashboard"
            />

            <FieldLabel text="Description" required />
            <textarea
              required
              minLength={20}
              maxLength={5000}
              rows={5}
              value={formState.description}
              onChange={(event) => setFormState((previous) => ({ ...previous, description: event.target.value }))}
              className={inputClassName}
              placeholder="Describe the issue, pain point, or request in enough detail for review."
            />

            <FieldLabel text="Steps to Reproduce" />
            <textarea
              rows={4}
              value={formState.stepsToReproduce}
              onChange={(event) => setFormState((previous) => ({ ...previous, stepsToReproduce: event.target.value }))}
              className={inputClassName}
              placeholder="If this is a bug, list the steps and what happened."
            />

            <FieldLabel text="Expected Outcome" />
            <textarea
              rows={3}
              value={formState.expectedOutcome}
              onChange={(event) => setFormState((previous) => ({ ...previous, expectedOutcome: event.target.value }))}
              className={inputClassName}
              placeholder="What should the system do instead?"
            />

            <div className="rounded-xl border border-blue-100 bg-blue-50 p-4 text-sm text-blue-900">
              Strong reports make prioritization easier:
              <ul className="mt-2 list-disc space-y-1 pl-5">
                <li>What you were trying to do</li>
                <li>What blocked you or felt inefficient</li>
                <li>How urgent it is for your team</li>
              </ul>
            </div>

            <Button type="submit" isLoading={submitting}>
              Submit Report
            </Button>
          </form>
        </section>

        <section className="rounded-2xl bg-white p-6 shadow">
          <div className="mb-5 flex items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Team Submission History</h2>
              <p className="mt-1 text-sm text-gray-600">
                Everyone in your manufacturer account can see what has already been reported and its current status.
              </p>
            </div>
            <Button variant="outline" onClick={() => navigate('/manufacturer')}>
              Dashboard
            </Button>
          </div>

          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((item) => (
                <div key={item} className="h-28 animate-pulse rounded-xl bg-gray-100" />
              ))}
            </div>
          ) : reports.length === 0 ? (
            <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-6 py-10 text-center">
              <p className="text-sm font-medium text-gray-700">No issue reports yet</p>
              <p className="mt-1 text-sm text-gray-500">Submit the first report when you hit a bug or want an improvement reviewed.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {reports.map((report) => {
                const status = getIssueReportStatusMeta(report.status);
                const priority = getIssueReportPriorityMeta(report.adminPriority);

                return (
                  <article key={report.id} className="rounded-2xl border border-gray-200 p-5">
                    <div className="flex flex-wrap items-center gap-2">
                      <StatusBadge tone={status.tone} size="sm">
                        {status.label}
                      </StatusBadge>
                      <StatusBadge tone={priority.tone} size="sm">
                        {priority.label}
                      </StatusBadge>
                      <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-gray-700">
                        {getIssueReportCategoryLabel(report.category)}
                      </span>
                    </div>

                    <div className="mt-3">
                      <h3 className="text-base font-semibold text-gray-900">{report.title}</h3>
                      <p className="mt-2 text-sm text-gray-700">{report.description}</p>
                    </div>

                    <dl className="mt-4 grid gap-3 sm:grid-cols-2">
                      <MetaItem label="Submitted By" value={`${report.reporter.fullName} (${getIssueReportReporterLabel(report.reporter.reporterType)})`} />
                      <MetaItem label="Created" value={new Date(report.createdAt).toLocaleString()} />
                      <MetaItem label="Affected Area" value={report.affectedArea || 'Not specified'} />
                      <MetaItem label="Last Updated" value={new Date(report.updatedAt).toLocaleString()} />
                    </dl>

                    {report.stepsToReproduce && (
                      <div className="mt-4 rounded-xl bg-gray-50 p-4">
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Steps to Reproduce</p>
                        <p className="mt-1 whitespace-pre-wrap text-sm text-gray-700">{report.stepsToReproduce}</p>
                      </div>
                    )}

                    {(report.adminNotes || report.resolutionSummary) && (
                      <div className="mt-4 grid gap-3 lg:grid-cols-2">
                        {report.adminNotes && (
                          <div className="rounded-xl bg-amber-50 p-4">
                            <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">Admin Notes</p>
                            <p className="mt-1 whitespace-pre-wrap text-sm text-amber-900">{report.adminNotes}</p>
                          </div>
                        )}
                        {report.resolutionSummary && (
                          <div className="rounded-xl bg-emerald-50 p-4">
                            <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Resolution / Delivery Notes</p>
                            <p className="mt-1 whitespace-pre-wrap text-sm text-emerald-900">{report.resolutionSummary}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function FieldLabel({ text, required = false }: { text: string; required?: boolean }) {
  return (
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {text}
      {required && <span className="ml-1 text-red-500">*</span>}
    </label>
  );
}

function SummaryCard({ label, value, toneClass }: { label: string; value: number; toneClass: string }) {
  return (
    <div className="rounded-2xl bg-white p-5 shadow">
      <div className={`inline-flex rounded-lg px-3 py-1 text-xs font-semibold uppercase tracking-wide ${toneClass}`}>
        {label}
      </div>
      <p className="mt-3 text-3xl font-bold text-gray-900">{value}</p>
    </div>
  );
}

function MetaItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</dt>
      <dd className="mt-1 text-sm text-gray-800">{value}</dd>
    </div>
  );
}
